import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import LayoutContainer from 'components/Common/LayoutContainer'
import ShareModal from 'components/Common/Modals/ShareModal'
import OfferBNPLAd from '../OfferBNPLAd'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getTacticalPackageOptions,
} from 'selectors/offerPage/offerPageSelectors'
import { shouldShowSalesUSP } from 'selectors/featuresSelectors'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import ContactUSP from '../ContactUSP'
import { ShareConfig } from 'components/Common/Modals/ShareModal/ShareModal'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import AlwaysOnPropertyDetails from '../AlwaysOn/AlwaysOnPropertyDetails'
import PropertySummary from './PropertySummary'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { useScreenSizeUp } from 'hooks/useScreenSize'

const OfferIntroWrapper = styled(LayoutContainer)`
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(20)} ${rem(40)};
  }

  ${mediaQueryUp.desktop} {
    padding: ${rem(30)} ${rem(12)};
  }
`

const OfferIntroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }

  ${mediaQueryUp.desktop} {
    grid-gap: ${rem(16)} ${rem(40)};
    grid-template-columns: 1fr min-content;
    grid-template-rows: min-content min-content min-content 1fr;
  }
`

const SummaryBlock = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    position: sticky;
    /* height of the filter bar */
    top: ${rem(64)};
    display: block;
    align-self: flex-start;
    grid-row: 3;
    grid-column: 2;
  }

  ${mediaQueryUp.desktop} {
    grid-row: 2 / span 3;
    grid-column: 2;
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const Summary = styled(PropertySummary)`
  display: block;
  align-self: flex-start;
  min-width: ${rem(290)};

  ${mediaQueryUp.desktop} {
    min-width: ${rem(370)};
  }
`

const OfferOtherActions = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    grid-row: 1;
    grid-column: 2;
  }
`

const MobileBNPLAd = styled(OfferBNPLAd)`
  margin-top: ${rem(24)};
  padding-top: ${rem(24)};
  border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  order: 1;

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const CenteredLabelGroup = styled(LabelGroup)`
  align-items: center;
`

interface Props {
  promotions: Array<App.OfferPackageOptionView>
  offer: App.HotelOffer
  onViewPackageOptions: () => void
  shouldShowSalesPhoneUSP: boolean
  shareConfig: ShareConfig
}

function PropertyIntro(props: Props) {
  const {
    offer,
    promotions,
    onViewPackageOptions,
    shouldShowSalesPhoneUSP,
    shareConfig,
  } = props

  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])

  const shouldShowToolTip = useScreenSizeUp('tablet')

  return (
    <OfferIntroWrapper>

      <VerticalSpacer gap={16}>
        <OfferIntroGrid data-testid="offer-intro">
          <CenteredLabelGroup>
            {offer.isAgentHubExclusive && <PromotionLabel key="agenthub_exclusive" variant="default">Agent Exclusive</PromotionLabel>}
          </CenteredLabelGroup>
          <OfferOtherActions>
            <TripGuard inverse>
              <ShareModal shareConfig={shareConfig}>
                <ShareWithFriendsButton horizontalOutdent="end" showToolTip={shouldShowToolTip} />
              </ShareModal>
            </TripGuard>
            <TripGuard>
              <ShareModal shareConfig={shareConfig}>
                <ShareWithFriendsButton showToolTip={shouldShowToolTip} />
              </ShareModal>
              <BookmarkButton offer={offer} />
            </TripGuard>
          </OfferOtherActions>
          <AlwaysOnPropertyDetails
            offer={offer}
            promotions={promotions}
          />
          <SummaryBlock>
            <div>
              <Summary lpcOffer={offer} onViewPackageOptions={onViewPackageOptions} />
            </div>
            <OfferBNPLAd prices={allPrices} />
          </SummaryBlock>
          <MobileBNPLAd prices={allPrices} />
        </OfferIntroGrid>
        {(shouldShowSalesPhoneUSP || promotions.length > 0) && <ContactUSP />}
      </VerticalSpacer>
    </OfferIntroWrapper>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  promotions: getTacticalPackageOptions(state, appState),
  shouldShowSalesPhoneUSP: shouldShowSalesUSP(appState, state.offer?.lowestPricePackage?.trackingPrice ?? 0, 300),
  open: !!state.activeFilter,
}), PropertyIntro, preventRenderWithOpenFilter)
